import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowDownIcon, ArrowUpIcon, DollarSign, ShoppingCart, Users, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface MetricCardProps {
  title: string;
  value: string;
  change: {
    value: string;
    trend: 'up' | 'down' | 'neutral';
  };
  description?: string;
  icon: React.ReactNode;
  isLoading?: boolean;
  breakdown?: Array<{
    name: string;
    value: number;
    percentage?: number;
  }>;
}

// Chart colors - using brand colors
const COLORS = ['#3fc060', '#ffcc4b']; // UberEats Green for Delivery, Brand Yellow for Pickup

// Format currency
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

// Custom tooltip component for metric breakdowns
const MetricBreakdownTooltip = ({ active, payload, title }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    // Use the percentage from our data instead of Recharts' calculated percentage
    const percentage = data.percentage !== undefined ? data.percentage.toFixed(1) : '0';
    
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3">
        <p className="font-medium text-sm mb-2">{data.name}</p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Value:</span>
            <span className="font-medium">
              {title === 'Average Order Value' || title === 'Total Revenue' ? 
                formatCurrency(Number(data.value.toFixed(2))) : 
                data.value.toLocaleString('en-NZ')}
            </span>
          </div>
          {data.percentage !== undefined && (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Percentage:</span>
              <span className="font-medium">{percentage}%</span>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return null;
};

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  change, 
  description, 
  icon, 
  isLoading,
  breakdown 
}) => {
  if (isLoading) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between pb-2 pt-4">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-8 w-8 rounded-full" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-24 mb-2" />
          <Skeleton className="h-4 w-32" />
        </CardContent>
      </Card>
    );
  }

  const cardContent = (
    <Card className="overflow-hidden card-hover">
      <CardHeader className="flex flex-row items-center justify-between pb-2 pt-4">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {breakdown && breakdown.length > 0 && (
            <Info className="h-3 w-3 text-muted-foreground" />
          )}
        </div>
        <div className="h-8 w-8 rounded-full bg-muted/50 p-1.5 flex items-center justify-center">
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <div className="flex items-center gap-1 mt-1">
          {change.trend === 'up' && (
            <ArrowUpIcon className="w-3 h-3 text-ubereats-green" />
          )}
          {change.trend === 'down' && (
            <ArrowDownIcon className="w-3 h-3 text-brand-red" />
          )}
          <p className={cn("text-xs font-medium", 
            change.trend === 'up' ? "text-ubereats-green" : 
            change.trend === 'down' ? "text-brand-red" : 
            "text-muted-foreground"
          )}>
            {change.value} vs previous period
          </p>
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-2">{description}</p>
        )}
      </CardContent>
    </Card>
  );

  if (breakdown && breakdown.length > 0) {
    return (
      <HoverCard openDelay={200}>
        <HoverCardTrigger asChild>
          {cardContent}
        </HoverCardTrigger>
        <HoverCardContent className="w-80" side="bottom" align="start">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">{title} Breakdown</h4>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={breakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {breakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<MetricBreakdownTooltip title={title} />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1 text-sm">
              {breakdown.map((item, index) => (
                <div key={index} className="flex justify-between">
                  <span className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-sm" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    {item.name}
                  </span>
                  <span className="font-medium">
                    {title === 'Average Order Value' || title === 'Total Revenue' ? 
                      formatCurrency(Number(item.value.toFixed(2))) : 
                      item.value.toLocaleString('en-NZ')} 
                    {item.percentage && ` (${item.percentage.toFixed(1)}%)`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  return cardContent;
};

interface DashboardMetricsProps {
  data: {
    totalOrders: number;
    totalRevenue: number;
    avgOrderValue: number;
    newCustomers: number;
    deliveryOrders?: number;
    pickupOrders?: number;
    deliveryRevenue?: number;
    pickupRevenue?: number;
    deliveryAvgOrderValue?: number;
    pickupAvgOrderValue?: number;
    deliveryNewCustomers?: number;
    pickupNewCustomers?: number;
  } | null;
  changes: {
    totalOrders: { value: string; trend: 'up' | 'down' | 'neutral' };
    totalRevenue: { value: string; trend: 'up' | 'down' | 'neutral' };
    avgOrderValue: { value: string; trend: 'up' | 'down' | 'neutral' };
    newCustomers: { value: string; trend: 'up' | 'down' | 'neutral' };
  } | null;
  isLoading: boolean;
}

const DashboardMetrics: React.FC<DashboardMetricsProps> = ({ data, changes, isLoading }) => {
  // Format large numbers with commas
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-NZ').format(value);
  };

  // Calculate breakdowns
  const orderBreakdown = data && (data.deliveryOrders !== undefined || data.pickupOrders !== undefined) ? [
    {
      name: 'Delivery',
      value: data.deliveryOrders || 0,
      percentage: data.totalOrders > 0 ? ((data.deliveryOrders || 0) / data.totalOrders) * 100 : 0
    },
    {
      name: 'Pickup',
      value: data.pickupOrders || 0,
      percentage: data.totalOrders > 0 ? ((data.pickupOrders || 0) / data.totalOrders) * 100 : 0
    }
  ] : undefined;

  const revenueBreakdown = data && (data.deliveryRevenue !== undefined || data.pickupRevenue !== undefined) ? [
    {
      name: 'Delivery',
      value: data.deliveryRevenue || 0,
      percentage: data.totalRevenue > 0 ? ((data.deliveryRevenue || 0) / data.totalRevenue) * 100 : 0
    },
    {
      name: 'Pickup',
      value: data.pickupRevenue || 0,
      percentage: data.totalRevenue > 0 ? ((data.pickupRevenue || 0) / data.totalRevenue) * 100 : 0
    }
  ] : undefined;

  const avgOrderBreakdown = data && (data.deliveryAvgOrderValue !== undefined || data.pickupAvgOrderValue !== undefined) ? [
    {
      name: 'Delivery',
      value: data.deliveryAvgOrderValue || 0
    },
    {
      name: 'Pickup',
      value: data.pickupAvgOrderValue || 0
    }
  ] : undefined;

  const customersBreakdown = data && (data.deliveryNewCustomers !== undefined || data.pickupNewCustomers !== undefined) ? [
    {
      name: 'Delivery',
      value: data.deliveryNewCustomers || 0,
      percentage: data.newCustomers > 0 ? ((data.deliveryNewCustomers || 0) / data.newCustomers) * 100 : 0
    },
    {
      name: 'Pickup',
      value: data.pickupNewCustomers || 0,
      percentage: data.newCustomers > 0 ? ((data.pickupNewCustomers || 0) / data.newCustomers) * 100 : 0
    }
  ] : undefined;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        title="Total Orders"
        value={data ? formatNumber(data.totalOrders) : '0'}
        change={changes?.totalOrders || { value: '0%', trend: 'neutral' }}
        icon={<ShoppingCart className="h-5 w-5" />}
        isLoading={isLoading}
        breakdown={orderBreakdown}
      />
      <MetricCard
        title="Average Order Value"
        value={data ? formatCurrency(data.avgOrderValue) : '$0.00'}
        change={changes?.avgOrderValue || { value: '0%', trend: 'neutral' }}
        icon={<DollarSign className="h-5 w-5" />}
        isLoading={isLoading}
        breakdown={avgOrderBreakdown}
      />
      <MetricCard
        title="Total Revenue"
        value={data ? formatCurrency(data.totalRevenue) : '$0.00'}
        change={changes?.totalRevenue || { value: '0%', trend: 'neutral' }}
        icon={<DollarSign className="h-5 w-5" />}
        isLoading={isLoading}
        breakdown={revenueBreakdown}
      />
      <MetricCard
        title="New Customers"
        value={data ? formatNumber(data.newCustomers) : '0'}
        change={changes?.newCustomers || { value: '0%', trend: 'neutral' }}
        description="Unique customers this period"
        icon={<Users className="h-5 w-5" />}
        isLoading={isLoading}
        breakdown={customersBreakdown}
      />
    </div>
  );
};

export default DashboardMetrics;
